import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, ChevronDown, BadgeCheck, Paperclip, Smile, ArrowDown, Loader2 } from "lucide-react";
import { api, formatApiError, timeAgo, uploadFile, fileUrl } from "../lib/api";

import ImageLightbox from "./ImageLightbox";
import { useVisualViewport, useIsMobile } from "../lib/useVisualViewport";

const SHORTCUTS = {
  employer: [
    "Thanks for applying, we're reviewing your application now.",
    "Could you confirm your availability for an interview this week?",
    "Could you share your right-to-work documents?",
    "We'll be in touch with a decision shortly.",
  ],
  candidate: [
    "Thank you, I look forward to hearing from you.",
    "I'm available for an interview this week.",
    "Could you share more details about the role?",
    "I've uploaded my latest CV to my profile.",
  ],
};

const EMOJIS = ["😀", "😊", "👍", "🙏", "🎉", "✅", "👋", "💼", "📅", "📍", "⏰", "❤️"];

function presence(lastSeen) {
  if (!lastSeen) return "";
  const mins = (Date.now() - new Date(lastSeen).getTime()) / 60000;
  if (mins < 5) return "Active now";
  if (mins < 60) return `Active ${Math.max(1, Math.round(mins))} min ago`;
  if (mins < 1440) return `Active ${Math.round(mins / 60)}h ago`;
  const d = new Date(lastSeen);
  return `Last active ${d.toLocaleDateString("en-GB", { day: "numeric", month: "short" })} at ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
}

function typicalText(mins) {
  if (mins === null || mins === undefined) return "";
  if (mins < 60) return `Typically replies within ~${Math.max(5, Math.round(mins / 5) * 5)} min`;
  if (mins < 1440) return `Typically replies within ~${Math.round(mins / 60)} hours`;
  return `Typically replies within ~${Math.round(mins / 1440)} days`;
}

const initials = (name) => (name || "?").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "?";
const GAP_MS = 30 * 60000;

/** Shared application chat thread (employer + candidate sides). `fill` makes the
    thread fill its parent's height (used by the mobile full-screen chat overlay);
    `onComposerFocus` reports keyboard state up so the parent can pin to the
    visible viewport. */
export default function ChatThread({ applicationId, viewerRole, testid, fill = false, onComposerFocus = null }) {
  const [messages, setMessages] = useState(null);
  const [meta, setMeta] = useState(null);
  const [infoOpen, setInfoOpen] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [newBelow, setNewBelow] = useState(0);
  const atBottomRef = useRef(true);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const fileRef = useRef(null);
  const wrapRef = useRef(null);
  // while the mobile keyboard is open, match the thread to the visible viewport so
  // the header stays on top and the composer sits right above the keyboard
  const isMobile = useIsMobile();
  const [composerFocused, setComposerFocused] = useState(false);
  const vv = useVisualViewport(isMobile && composerFocused);
  const base = viewerRole === "employer"
    ? `/employer/applications/${applicationId}/messages`
    : `/candidate/applications/${applicationId}/messages`;

  const load = useCallback(() => {
    api.get(base).then((r) => setMessages(r.data)).catch(() => {});
  }, [base]);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    setMeta(null);
    setInfoOpen(false);
    api.get(`/messages/thread-meta/${applicationId}`).then((r) => setMeta(r.data)).catch(() => {});
  }, [applicationId]);

  const scrollToLatest = () => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    setNewBelow(0);
    atBottomRef.current = true;
  };

  const onListScroll = () => {
    const el = listRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    atBottomRef.current = atBottom;
    if (atBottom) setNewBelow(0);
  };

  // auto-scroll only when already at the bottom; scrolled up reading = show the
  // "new messages" pill instead of yanking the view down
  const prevLenRef = useRef(0);
  useEffect(() => {
    const n = (messages || []).length;
    const prev = prevLenRef.current;
    prevLenRef.current = n;
    if (!n) return;
    if (prev > 0 && n > prev && !atBottomRef.current) {
      const incoming = messages.slice(prev).filter((m) => m.sender_role !== viewerRole).length;
      if (incoming > 0) {
        setNewBelow((c) => c + incoming);
        return;
      }
    }
    scrollToLatest();
  }, [messages]);

  useEffect(() => { prevLenRef.current = 0; setNewBelow(0); atBottomRef.current = true; }, [applicationId]);

  const send = async (body, attachment) => {
    const msg = (body ?? text).trim();
    if ((!msg && !attachment) || sending) return;
    setSending(true);
    try {
      await api.post(base, { body: msg, ...(attachment ? { attachment } : {}) });
      setText("");
      setEmojiOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const attach = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || uploading) return;
    setUploading(true);
    try {
      const up = await uploadFile(f, "chat");
      await send(undefined, { path: up.path });
    } catch (err) {
      toast.error(err.message || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const pres = meta ? presence(meta.last_seen) : "";
  const typical = meta ? typicalText(meta.typical_reply_minutes) : "";
  const theirInitials = initials(meta?.name);
  const myLabel = viewerRole === "employer" ? (meta?.viewer_company || "You") : "You";

  return (
    <div ref={wrapRef} data-testid={testid}
      style={!fill && vv ? { height: Math.max(300, vv.height - 72), minHeight: 0 } : undefined}
      className={fill
        ? "flex flex-col flex-1 min-h-0 h-full bg-white overflow-hidden"
        : "flex flex-col h-[calc(100dvh-240px)] min-h-[440px] lg:h-[620px] border border-zinc-200 rounded-xl bg-white overflow-hidden shadow-sm scroll-mt-20"}>
      {meta && (
        <div className="relative shrink-0 flex items-center gap-3 px-4 sm:px-6 py-3 border-b border-zinc-100 bg-white/95 backdrop-blur-md z-10" data-testid={`${testid}-header`}>
          <div className="h-10 w-10 rounded-full bg-zinc-900 text-white text-sm font-bold inline-flex items-center justify-center shrink-0 shadow-sm">
            {theirInitials}
          </div>
          <div className="min-w-0 flex-1">
            {meta.profile_url ? (
              <a href={meta.profile_url} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-profile-link`}
                className="text-lg font-bold text-zinc-900 hover:text-blue-700 transition-colors duration-150 truncate inline-flex items-center gap-1.5 max-w-full">
                <span className="truncate">{meta.name}</span>
                {meta.verified && <BadgeCheck title="Verified" data-testid={`${testid}-verified-badge`} className="h-4 w-4 text-blue-600 shrink-0" />}
              </a>
            ) : (
              <button type="button" onClick={() => setInfoOpen(!infoOpen)} data-testid={`${testid}-profile-toggle`}
                className="text-lg font-bold text-zinc-900 hover:text-blue-700 transition-colors duration-150 inline-flex items-center gap-1.5 max-w-full">
                <span className="truncate">{meta.name}</span>
                {meta.verified && <BadgeCheck title="Verified" data-testid={`${testid}-verified-badge`} className="h-4 w-4 text-blue-600 shrink-0" />}
                <ChevronDown className={`h-3.5 w-3.5 text-zinc-400 transition-transform duration-150 ${infoOpen ? "rotate-180" : ""}`} />
              </button>
            )}
            <p className="text-xs text-zinc-500 flex items-center gap-1.5 mt-0.5" data-testid={`${testid}-presence`}>
              {pres === "Active now" && <span className="h-2 w-2 rounded-full bg-green-500 inline-block" />}
              {pres}
              {typical && <span className="hidden sm:inline text-zinc-400">· {typical}</span>}
            </p>
          </div>
          {typical && (
            <span className="sm:hidden text-[11px] text-zinc-400 shrink-0" data-testid={`${testid}-typical`}>{typical}</span>
          )}
          {infoOpen && (
            <div className="absolute left-4 top-full mt-1 z-20 w-64 border border-zinc-200 rounded-md bg-white shadow-lg p-3.5" data-testid={`${testid}-profile-popover`}>
              <p className="text-sm font-semibold text-zinc-950 inline-flex items-center gap-1.5">
                {meta.name}
                {meta.verified && <BadgeCheck className="h-4 w-4 text-blue-600 shrink-0" />}
              </p>
              {meta.verified && <p className="text-[11px] font-medium text-blue-700 mt-0.5">Verified candidate</p>}
              <p className="text-xs text-zinc-500 mt-1">{pres || "No recent activity"}</p>
              {typical && <p className="text-xs text-zinc-500 mt-0.5">{typical}</p>}
              {meta.application_status && <p className="text-xs text-zinc-500 mt-0.5 capitalize">Application: {meta.application_status}</p>}
              {meta.applied_at && <p className="text-xs text-zinc-400 mt-0.5">Applied {timeAgo(meta.applied_at)}</p>}
            </div>
          )}
        </div>
      )}
      <div className="relative flex-1 min-h-0">
      <div ref={listRef} onScroll={onListScroll} className="h-full overflow-y-auto px-4 sm:px-6 py-5 flex flex-col gap-4 bg-white" data-testid={`${testid}-list`}>
        {!messages ? (
          <div className="h-16 bg-zinc-50 rounded-xl animate-pulse" />
        ) : messages.length === 0 ? (
          <p className="text-xs text-zinc-400 text-center py-4">No messages yet, say hello.</p>
        ) : messages.map((m, i) => {
          const mine = m.sender_role === viewerRole;
          const prev = messages[i - 1];
          const showStamp = !prev || (new Date(m.created_at) - new Date(prev.created_at)) > GAP_MS;
          return (
            <div key={m.id} className="contents">
              {showStamp && (
                <div className="flex justify-center my-1">
                  <span className="text-xs font-medium text-zinc-400 bg-zinc-50 px-3 py-1 rounded-full border border-zinc-100">
                    {new Date(m.created_at).toLocaleTimeString("en-GB", { hour: "numeric", minute: "2-digit" })}
                  </span>
                </div>
              )}
              <div className={`flex items-end gap-2.5 max-w-[85%] ${mine ? "self-end flex-row-reverse" : "self-start"}`}
                data-testid={`${testid}-msg-${m.id}`}>
                <div className="w-8 h-8 rounded-full bg-zinc-900 text-white inline-flex items-center justify-center text-[10px] font-bold shrink-0 mb-0.5 shadow-sm">
                  {mine ? initials(myLabel === "You" ? "You" : myLabel) : theirInitials}
                </div>
                <div className={`px-4 py-2.5 text-[15px] leading-relaxed ${
                  mine
                    ? "bg-blue-600 text-white rounded-[20px] rounded-br-sm shadow-sm shadow-blue-600/10"
                    : "bg-zinc-100 text-zinc-900 rounded-[20px] rounded-bl-sm border border-zinc-200/50"}`}>
                  {m.attachment && (m.attachment.content_type || "").startsWith("image/") && (
                    <button type="button" onClick={() => setViewer({ src: fileUrl(m.attachment.path), alt: m.attachment.filename })}
                      data-testid={`${testid}-attach-img-${m.id}`} className="block">
                      <img src={fileUrl(m.attachment.path)} alt={m.attachment.filename}
                        className="rounded-lg max-h-48 max-w-full mb-1.5 border border-black/10" />
                    </button>
                  )}
                  {m.attachment && !(m.attachment.content_type || "").startsWith("image/") && (
                    <a href={fileUrl(m.attachment.path)} target="_blank" rel="noopener noreferrer"
                      data-testid={`${testid}-attach-file-${m.id}`}
                      className={`inline-flex items-center gap-2 mb-1.5 text-sm font-semibold underline underline-offset-2 ${mine ? "text-white" : "text-blue-700"}`}>
                      <Paperclip className="h-3.5 w-3.5 shrink-0" /> {m.attachment.filename}
                    </a>
                  )}
                  <p className="whitespace-pre-wrap break-words">{m.body}</p>
                  <p className={`text-[10px] mt-1 ${mine ? "text-blue-200" : "text-zinc-400"}`}>
                    {mine ? "You" : m.sender_name || (m.sender_role === "employer" ? "Employer" : "Candidate")} · {timeAgo(m.created_at)}
                    {mine && m.read_at ? " · read" : ""}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {newBelow > 0 && (
        <button onClick={scrollToLatest} data-testid={`${testid}-new-below`}
          className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 inline-flex items-center gap-1.5 bg-blue-600 text-white text-xs font-semibold px-3.5 py-1.5 rounded-full shadow-lg hover:bg-blue-700 transition-colors duration-200">
          <ArrowDown className="h-3.5 w-3.5" /> {newBelow} new message{newBelow === 1 ? "" : "s"}
        </button>
      )}
      </div>
      {!meta?.closed && (
      <div className="shrink-0 border-t border-zinc-100 px-4 py-3 flex flex-nowrap overflow-x-auto gap-2 sm:flex-wrap bg-zinc-50/50" data-testid={`${testid}-shortcuts`}>
        {SHORTCUTS[viewerRole].map((s, i) => (
          <button key={`shortcut-${i}`} type="button" onClick={() => send(s)} disabled={sending}
            data-testid={`${testid}-shortcut-${i}`}
            className="shrink-0 whitespace-nowrap rounded-full bg-white border border-blue-200 text-blue-600 px-4 py-1.5 text-sm hover:bg-blue-50 transition-colors duration-200 shadow-sm disabled:opacity-50">
            {s}
          </button>
        ))}
      </div>
      )}
      {meta?.closed ? (
        <div className="shrink-0 p-4 bg-zinc-50 border-t border-zinc-100 text-center text-xs text-zinc-500"
          data-testid={`${testid}-closed`}>
          This conversation has been deleted and can no longer receive messages.
        </div>
      ) : (
      <form onSubmit={(e) => { e.preventDefault(); send(); }}
        className="shrink-0 p-3 sm:p-4 bg-white border-t border-zinc-100 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <div className="flex items-end gap-2 bg-zinc-100/80 rounded-3xl p-1.5 border border-transparent">
          <input ref={inputRef} value={text} onChange={(e) => setText(e.target.value)} maxLength={2000}
            placeholder="Type a message…" data-testid={`${testid}-input`}
            onFocus={() => {
              setComposerFocused(true);
              onComposerFocus?.(true);
              // after the keyboard animation, pin the thread to the top of the visible area
              setTimeout(() => wrapRef.current?.scrollIntoView({ block: "start", behavior: "smooth" }), 350);
            }}
            onBlur={() => { setComposerFocused(false); onComposerFocus?.(false); }}
            className="flex-1 bg-transparent border-none outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus:shadow-none text-base placeholder:text-zinc-500 py-2.5 pl-4" />
          <div className="relative flex items-center gap-0.5 shrink-0 pb-1 pr-1.5">
            <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" className="hidden"
              data-testid={`${testid}-attach-input`} onChange={attach} />
            <button type="button" data-testid={`${testid}-attach`} disabled={uploading}
              onClick={() => fileRef.current?.click()}
              title="Attach a file (JPG, PNG, WEBP or PDF, 8 MB max)"
              className="p-2 hover:bg-zinc-200/50 rounded-full transition-colors duration-200 text-zinc-500 hover:text-zinc-700 disabled:opacity-50">
              {uploading ? <Loader2 className="h-[18px] w-[18px] animate-spin" /> : <Paperclip className="h-[18px] w-[18px]" />}
            </button>
            <button type="button" onClick={() => setEmojiOpen(!emojiOpen)} data-testid={`${testid}-emoji`}
              className="p-2 hover:bg-zinc-200/50 rounded-full transition-colors duration-200 text-zinc-500 hover:text-zinc-700">
              <Smile className="h-[18px] w-[18px]" />
            </button>
            {emojiOpen && (
              <div className="absolute bottom-full right-0 mb-2 z-20 bg-white border border-zinc-200 rounded-xl shadow-lg p-2 grid grid-cols-6 gap-1" data-testid={`${testid}-emoji-panel`}>
                {EMOJIS.map((e) => (
                  <button key={e} type="button" data-testid={`${testid}-emoji-${e}`}
                    onClick={() => { setText((t) => (t + e).slice(0, 2000)); setEmojiOpen(false); inputRef.current?.focus(); }}
                    className="h-8 w-8 text-lg hover:bg-zinc-100 rounded-md transition-colors duration-150">
                    {e}
                  </button>
                ))}
              </div>
            )}
            <button type="submit" disabled={sending || !text.trim()} data-testid={`${testid}-send`}
              className="bg-blue-600 hover:bg-blue-700 text-white w-9 h-9 rounded-full inline-flex items-center justify-center transition-all duration-200 active:scale-95 ml-1 mb-0.5 shadow-sm disabled:opacity-50">
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      </form>
      )}
      {viewer && <ImageLightbox src={viewer.src} alt={viewer.alt} testid={`${testid}-viewer`} onClose={() => setViewer(null)} />}
    </div>
  );
}
